import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { getTransformedCloudinaryUrl } from "../utils/cloudinary";

const ImageSlider = ({ slides, onModalStateChange }) => {
  const [current, setCurrent] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState([]);

  useEffect(() => {
    if (onModalStateChange) onModalStateChange(expanded);
  }, [expanded, onModalStateChange]);

  useEffect(() => {
    setLoaded(slides.map(() => false));
    setCurrent(0);
  }, [slides]);

  useEffect(() => {
    let mounted = true;
    if (!slides || slides.length === 0) return () => (mounted = false);

    const toPreload = new Set([current]);
    if (slides.length > 1) {
      toPreload.add((current + 1) % slides.length);
      toPreload.add((current - 1 + slides.length) % slides.length);
    }

    const links = [];
    const imgs = [];

    toPreload.forEach((idx) => {
      const url = slides[idx]?.url;
      if (!url) return;

      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = url;
      document.head.appendChild(link);
      links.push(link);

      if (!loaded[idx]) {
        const img = new window.Image();
        img.src = url;
        img.onload = () => {
          if (!mounted) return;
          setLoaded((prev) => {
            const next = prev.length ? [...prev] : slides.map(() => false);
            next[idx] = true;
            return next;
          });
        };
        img.onerror = () => {
          if (!mounted) return;
          setLoaded((prev) => {
            const next = prev.length ? [...prev] : slides.map(() => false);
            next[idx] = true;
            return next;
          });
        };
        imgs.push(img);
      }
    });

    return () => {
      mounted = false;
      links.forEach((l) => {
        try {
          document.head.removeChild(l);
        } catch (e) {}
      });
      imgs.forEach((i) => {
        i.onload = null;
        i.onerror = null;
      });
    };
  }, [current, slides, loaded]);

  const goPrev = () =>
    setCurrent((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  const goNext = () =>
    setCurrent((prev) => (prev === slides.length - 1 ? 0 : prev + 1));

  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchEndX - touchStartX;
    if (diff > 50) {
      goPrev();
    } else if (diff < -50) {
      goNext();
    }
    setTouchStartX(null);
  };

  const [modalTouchStartX, setModalTouchStartX] = useState(null);
  const handleModalTouchStart = (e) => {
    setModalTouchStartX(e.touches[0].clientX);
  };
  const handleModalTouchEnd = (e) => {
    if (modalTouchStartX === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchEndX - modalTouchStartX;
    if (diff > 50) {
      goPrev();
    } else if (diff < -50) {
      goNext();
    }
    setModalTouchStartX(null);
  };

  const Spinner = () => (
    <div className="flex items-center justify-center w-full h-full">
      <svg
        className="animate-spin h-12 w-12 text-indigo-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        />
      </svg>
    </div>
  );

  return (
    <>
      <div className="w-full flex flex-col items-center mb-8">
        <h2 className="text-3xl font-bold mb-4">
          {slides[current].title.split("\n").map((line, idx) => (
            <React.Fragment key={idx}>
              {line}
              {idx < slides[current].title.split("\n").length - 1 && <br />}
            </React.Fragment>
          ))}
        </h2>
        <div
          className="relative w-full max-w-[16rem] sm:max-w-xs md:max-w-sm lg:max-w-md xl:max-w-lg flex flex-col items-center"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {!expanded && (
            <button
              className="absolute top-2 right-2 z-20 bg-black/80 text-black px-3 py-1 rounded-full font-semibold shadow transition hover:bg-white hover:text-black"
              onClick={() => setExpanded(true)}
            >
              Maximize Image
            </button>
          )}
          {!loaded[current] ? (
            <div className="rounded-3xl shadow-lg w-full aspect-square bg-white flex items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <img
              src={getTransformedCloudinaryUrl(
                slides[current].url,
                "w_1000,q_auto"
              )}
              alt={slides[current].title}
              className="rounded-3xl shadow-lg w-full object-cover aspect-square"
            />
          )}
          <div className="absolute inset-0 flex items-center justify-between px-2">
            <button
              onClick={goPrev}
              className="bg-white/80 hover:bg-white text-gray-900 rounded-full p-2 shadow transition"
              aria-label="Previous"
            >
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  d="M15 19l-7-7 7-7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              onClick={goNext}
              className="bg-white/80 hover:bg-white text-gray-900 rounded-full p-2 shadow transition"
              aria-label="Next"
            >
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  d="M9 5l7 7-7 7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrent(idx)}
              className={`w-3 h-3 rounded-full ${
                idx === current ? "bg-indigo-600" : "bg-gray-300"
              }`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      </div>
      {expanded &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center"
            onClick={() => setExpanded(false)}
          >
            <div
              className="relative max-w-2xl w-full flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={handleModalTouchStart}
              onTouchEnd={handleModalTouchEnd}
            >
              <button
                className="absolute top-2 right-2 z-10 bg-black/80 text-black rounded-full p-2 shadow transition hover:bg-white hover:text-black"
                onClick={() => setExpanded(false)}
                aria-label="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line
                    x1="18"
                    y1="6"
                    x2="6"
                    y2="18"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <line
                    x1="6"
                    y1="6"
                    x2="18"
                    y2="18"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </button>
              <div
                className="flex items-center justify-center w-full"
                style={{
                  width: "100%",
                  maxWidth: "1000px",
                  maxHeight: "min(1000px, calc(100vh - 96px))",
                }}
              >
                {!loaded[current] ? (
                  <div
                    className="rounded-3xl shadow-2xl bg-white w-full flex items-center justify-center"
                    style={{
                      maxWidth: "1000px",
                      maxHeight: "min(1000px, calc(100vh - 96px))",
                    }}
                  >
                    <Spinner />
                  </div>
                ) : (
                  <img
                    src={getTransformedCloudinaryUrl(
                      slides[current].url,
                      "w_1000,q_auto"
                    )}
                    alt={slides[current].title}
                    className="rounded-3xl shadow-2xl bg-white"
                    style={{
                      width: "100%",
                      height: "auto",
                      maxWidth: "1000px",
                      maxHeight: "min(1000px, calc(100vh - 96px))",
                      objectFit: "contain",
                    }}
                  />
                )}
              </div>
              <div className="w-full flex flex-col items-center mt-4">
                <div className="flex flex-col items-center bg-gray-100 rounded-2xl shadow px-4 py-3 max-w-xl w-full">
                  <div className="flex items-center justify-between w-full">
                    <button
                      onClick={goPrev}
                      className="text-black rounded-full p-2 transition"
                      aria-label="Previous"
                    >
                      <svg
                        width="32"
                        height="32"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          d="M21 28l-9-9 9-9"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <span className="text-black text-lg font-semibold px-4 text-center flex-1">
                      {slides[current].title.split("\n").map((line, idx) => (
                        <React.Fragment key={idx}>
                          {line}
                          {idx <
                            slides[current].title.split("\n").length - 1 && (
                            <br />
                          )}
                        </React.Fragment>
                      ))}
                    </span>
                    <button
                      onClick={goNext}
                      className="text-black rounded-full p-2 transition"
                      aria-label="Next"
                    >
                      <svg
                        width="32"
                        height="32"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          d="M11 4l9 9-9 9"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                  <div className="flex gap-2 mt-3 justify-center w-full">
                    {slides.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrent(idx)}
                        className={`w-3 h-3 rounded-full ${
                          idx === current ? "bg-indigo-600" : "bg-gray-400"
                        }`}
                        aria-label={`Go to slide ${idx + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: -9999,
          width: 1,
          height: 1,
          overflow: "hidden",
          pointerEvents: "none",
          opacity: 0,
        }}
      >
        {slides.map((s, idx) => {
          if (idx === current || (loaded && loaded[idx])) return null;
          return (
            <img
              key={idx}
              src={getTransformedCloudinaryUrl(s.url, "w_800,q_auto")}
              alt=""
            />
          );
        })}
      </div>
    </>
  );
};

export default ImageSlider;
